import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { X } from 'lucide-react'

import { cn } from '@/lib/cn'
import { IconButton } from './Button'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const SIZES = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' }

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  // Escape zamyka, a przewijanie tła jest blokowane na czas otwarcia.
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Fokus wędruje do okna, żeby czytnik ekranu ogłosił jego treść.
    dialogRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-secondary/40 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          // Kolumna z przewijaną częścią środkową — nagłówek i przyciski
          // zostają widoczne niezależnie od długości formularza.
          'flex max-h-[92dvh] w-full flex-col rounded-t-2xl bg-surface shadow-ambient sm:rounded-2xl',
          // `max-w-full` i `min-w-0` trzymają okno w granicach ekranu: bez nich
          // szeroka zawartość (siatka pól, tabela) rozpycha modal poza telefon
          // i nie da się dosięgnąć prawej krawędzi formularza.
          'min-w-0 max-w-full overflow-hidden',
          'animate-scale-in focus:outline-none',
          SIZES[size],
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-outline/10 px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="font-headline text-lg font-bold text-secondary sm:text-xl">{title}</h2>
            {description && <p className="mt-1 text-sm text-on-surface-variant">{description}</p>}
          </div>
          <IconButton label="Zamknij" icon={<X size={18} />} onClick={onClose} />
        </header>

        {/* `overflow-x-hidden` domyka sprawę: pojedyncze zbyt szerokie dziecko
            nie wypycha wtedy całego okna, tylko zwęża się do dostępnej szerokości. */}
        <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 sm:px-6 sm:py-6">
          {children}
        </div>

        {footer && (
          <footer className="flex shrink-0 flex-wrap justify-end gap-3 border-t border-outline/10 bg-surface-container-low/40 px-4 py-4 sm:px-6">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}

interface ConfirmDialogProps {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  isLoading?: boolean
  onConfirm: () => void
  onClose: () => void
}

/** Potwierdzenie operacji nieodwracalnej lub wpływającej na dostęp. */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Potwierdź',
  isLoading,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-6 py-2.5 text-sm font-bold text-on-surface-variant transition-colors hover:bg-surface-container"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="rounded-lg bg-error px-6 py-2.5 text-sm font-bold text-on-error transition-colors hover:bg-error/90 disabled:opacity-60"
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-on-surface-variant">{message}</p>
    </Modal>
  )
}
